// File: src/pages/teams/components/modals/EmailCampaignDetailsModal.jsx
import React, { useEffect, useState } from 'react';
import { collection, doc, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { safeOnSnapshot } from '../../../../../utils/firestoreSubscriptions';
import SubmitButton from '../../../../../components/forms/formComponents/SubmitButton';
import { db, functions } from '../../../../../firebase-client';
import EmailReplyThreads from './emailReplyThreads';
import '../../../teams.css';
import { emailPreviewHtml } from '../../../../../utils/emailContent';

function pct(n, d) {
  if (!d || d <= 0) return '—';
  return `${Math.min(100, Math.round((n / d) * 100))}%`;
}

function toDate(value) {
  const date = value?.toDate?.() || value || null;
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
}

function campaignContentHtml(campaign = {}) {
  return String(campaign?.html || campaign?.contentHtml || campaign?.bodyHtml || '').trim();
}

export default function EmailCampaignDetailsModal({
  isOpen,
  onClose,
  teamId,
  campaign,     // full campaign doc
  canReply = false,
  currentUserId = '',
  memberProfiles = [],
}) {
  const [liveCampaign, setLiveCampaign] = useState(campaign);
  const [replies, setReplies] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [activeReplyId, setActiveReplyId] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [replyError, setReplyError] = useState('');

  useEffect(() => {
    if (!isOpen || !teamId || !campaign?.id) return undefined;
    setLiveCampaign(campaign);
    setReplies([]);
    setRecipients([]);
    setActiveReplyId('');
    setReplyBody('');
    setReplyError('');

    const campaignRef = doc(db, 'teams', teamId, 'emailCampaigns', campaign.id);
    const unsubscribeCampaign = safeOnSnapshot(campaignRef, (snap) => {
      if (snap.exists()) setLiveCampaign({ id: snap.id, ...snap.data() });
    });
    const repliesRef = query(
      collection(db, 'teams', teamId, 'emailCampaigns', campaign.id, 'replies'),
      orderBy('createdAt', 'asc')
    );
    const unsubscribeReplies = safeOnSnapshot(repliesRef, (snap) => {
      setReplies(snap.docs.map((replyDoc) => ({ id: replyDoc.id, ...replyDoc.data() })));
    });
    const recipientsRef = collection(db, 'teams', teamId, 'emailCampaigns', campaign.id, 'recipients');
    const unsubscribeRecipients = safeOnSnapshot(recipientsRef, (snap) => {
      setRecipients(snap.docs.map((recipientDoc) => ({ id: recipientDoc.id, ...recipientDoc.data() })));
    });

    return () => {
      unsubscribeCampaign();
      unsubscribeReplies();
      unsubscribeRecipients();
    };
  }, [campaign, campaign?.id, isOpen, teamId]);

  if (!isOpen || !campaign) return null;

  const currentCampaign = liveCampaign || campaign;
  const sentAt = toDate(currentCampaign?.sentAt);
  const totals = currentCampaign?.totals || {};
  const delivered = totals.delivered || 0;
  const openedUnique = totals.openedUnique || 0;
  const clickedUnique = totals.clickedUnique || 0;
  const bounced = totals.bounced || 0;
  const inboundReplies = totals.inboundReplies || totals.replies || replies.filter((reply) => reply.direction === 'inbound').length;

  const audience = currentCampaign?.audience || {};
  const tagFilters = audience?.tagFilters || [];
  const explicitIds = audience?.explicitMemberIds || [];
  const estReach = currentCampaign?.estReach ?? currentCampaign?.audienceCount;
  const originalHtml = campaignContentHtml(currentCampaign);
  const emailKind = currentCampaign?.type === 'directMember' ? 'Individual email' : 'Email';
  const inboundReplyTargets = replies.filter((reply) => reply.direction === 'inbound');
  const activeReply = activeReplyId
    ? inboundReplyTargets.find((reply) => reply.id === activeReplyId) || null
    : null;
  const recipientNameById = recipients.reduce((acc, recipient) => {
    const name = String(recipient?.name || '').trim();
    if (recipient.id && name) acc[recipient.id] = name;
    return acc;
  }, {});
  memberProfiles.forEach((member) => {
    const name = String(member?.name || member?.displayName || '').trim();
    if (member?.id && name && !recipientNameById[member.id]) recipientNameById[member.id] = name;
  });

  const handleSendReply = async () => {
    const body = replyBody.trim();
    if (!body || !activeReply?.id || sendingReply) return;
    setReplyError('');
    setSendingReply(true);
    try {
      const call = httpsCallable(functions, 'sendCampaignReply');
      await call({
        teamId,
        campaignId: currentCampaign.id,
        replyId: activeReply.id,
        body,
      });
      setReplyBody('');
      setActiveReplyId('');
    } catch (error) {
      setReplyError('Could not send this reply. Please try again.');
    } finally {
      setSendingReply(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-content email-campaign-details-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="email-campaign-details-modal__body">
          <h2 style={{ marginBottom: '0.25rem' }}>
            {currentCampaign?.name || currentCampaign?.subject || emailKind}
          </h2>
          <div className="text-muted" style={{ marginBottom: '0.75rem' }}>
            {emailKind} • {sentAt ? sentAt.toLocaleString() : '—'} • {currentCampaign?.status || 'unknown'}
          </div>

          <div className="grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: '0.75rem' }}>
            <div className="stat card" style={{ padding: '0.75rem' }}>
              <div className="text-muted" style={{ fontSize: '0.85rem' }}>Delivered</div>
              <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{delivered}</div>
            </div>
            <div className="stat card" style={{ padding: '0.75rem' }}>
              <div className="text-muted" style={{ fontSize: '0.85rem' }}>Unique Opens</div>
              <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                {openedUnique} ({pct(openedUnique, delivered)})
              </div>
            </div>
            <div className="stat card" style={{ padding: '0.75rem' }}>
              <div className="text-muted" style={{ fontSize: '0.85rem' }}>Unique Clicks</div>
              <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{clickedUnique}</div>
            </div>
            <div className="stat card" style={{ padding: '0.75rem' }}>
              <div className="text-muted" style={{ fontSize: '0.85rem' }}>Bounced</div>
              <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{bounced}</div>
            </div>
            <div className="stat card" style={{ padding: '0.75rem' }}>
              <div className="text-muted" style={{ fontSize: '0.85rem' }}>Replies</div>
              <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{inboundReplies}</div>
            </div>
          </div>

          <details className="email-campaign-section">
            <summary>
              <span>Audience</span>
              <span>Estimated reach {estReach ?? '—'}</span>
            </summary>
            <div className="email-campaign-section__body">
              {tagFilters?.length > 0 && (
                <>
                  <div className="text-muted" style={{ fontSize: '0.9rem' }}>Tag filters:</div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                    {tagFilters.map((t, i) => (
                      <span
                        key={i}
                        className="badge"
                        style={{
                          border: '1px solid var(--team-primary)',
                          borderRadius: '999px',
                          padding: '0.15rem 0.5rem',
                          fontSize: '0.85rem'
                        }}
                        title={t?.category ? `${t.category}: ${t.name}` : t?.name || String(t)}
                      >
                        {t?.name || String(t)}
                      </span>
                    ))}
                  </div>
                </>
              )}

              {explicitIds?.length > 0 && (
                <div className="text-muted" style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  Directly selected member{explicitIds.length === 1 ? '' : 's'}: <strong>{explicitIds.length}</strong>.
                </div>
              )}
              {audience?.all && (
                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                  Audience: <strong>All members</strong>.
                </div>
              )}
              {!audience?.all && tagFilters.length === 0 && explicitIds.length === 0 && (
                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                  No detailed audience filters stored for this campaign.
                </div>
              )}
            </div>
          </details>

          <details className="email-campaign-section">
            <summary>
              <span>Content</span>
              <span>{currentCampaign?.subject || 'No subject'}</span>
            </summary>
            <div className="email-campaign-section__body">
              {currentCampaign?.subject && (
                <div className="email-campaign-field">
                  <strong>Subject</strong>
                  <span>{currentCampaign.subject}</span>
                </div>
              )}
              {currentCampaign?.previewText && (
                <div className="email-campaign-field">
                  <strong>Preview</strong>
                  <span>{currentCampaign.previewText}</span>
                </div>
              )}
              <div className="email-campaign-field">
                <strong>Body</strong>
                {originalHtml ? (
                  <iframe
                    title="Original email content"
                    className="email-campaign-original-body"
                    sandbox=""
                    referrerPolicy="no-referrer"
                    srcDoc={emailPreviewHtml(originalHtml)}
                    style={{ width: '100%', height: 420, background: '#fff', boxSizing: 'border-box' }}
                  />
                ) : (
                  <span className="text-muted">Original body was not stored for this campaign.</span>
                )}
              </div>
            </div>
          </details>

          <EmailReplyThreads
            replies={replies}
            inboundReplies={inboundReplies}
            canReply={canReply}
            teamId={teamId}
            campaignId={currentCampaign.id}
            currentUserId={currentUserId}
            recipientNameById={recipientNameById}
            activeReplyId={activeReplyId}
            replyBody={replyBody}
            sendingReply={sendingReply}
            replyError={replyError}
            onActiveReplyChange={(replyId) => {
              setActiveReplyId(replyId);
              setReplyError('');
            }}
            onReplyBodyChange={setReplyBody}
            onSendReply={handleSendReply}
          />
        </div>

        <div className="form-actions email-campaign-details-modal__footer">
          <SubmitButton type="button" onClick={onClose} label="Close" variant="secondary" />
        </div>
      </div>
    </div>
  );
}
